/**
 * FilterPage - Phoenix Premium Design
 *
 * Main page for requesting electoral analysis using dropdown filters.
 * Brand color: #00468C (Phoenix blue)
 *
 * Based on: docs/design/UI_IMPROVEMENTS_PLAN.md
 */

import React, { useState, useEffect } from 'react';
import ElectionFilter from './ElectionFilter';
import { requestAnalysis } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import { getUserCredits, deductCredit } from '../../services/userService';

interface FilterState {
  electionType: string;
  municipalityCode: string;
  municipalityName: string;
  party: string;
  socialPulse: boolean;
  clientPartyIsNew: boolean;
}

// Icons
const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const CandidateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const AdminIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const HelpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const CreditCardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const SurveyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M9 11l3 3L22 4"/>
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
  </svg>
);

// Close icon for modal
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

// Rocket icon for success modal
const RocketIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-12 h-12">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
  </svg>
);

const FilterPage: React.FC = () => {
  const { user, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();

  // State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState<string>(() => {
    try { return sessionStorage.getItem('phoenix_additional_info') || ''; } catch { return ''; }
  });
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [credits, setCredits] = useState<number>(0);
  const [resetTrigger, setResetTrigger] = useState<number>(0);

  // Persist additionalInfo to sessionStorage
  useEffect(() => {
    try { sessionStorage.setItem('phoenix_additional_info', additionalInfo); } catch {}
  }, [additionalInfo]);

  // Load user credits on mount
  useEffect(() => {
    const loadCredits = async () => {
      if (user?.uid) {
        const userCredits = await getUserCredits(user.uid);
        setCredits(userCredits);
      }
    };
    loadCredits();
  }, [user?.uid]);

  // Handle filter changes
  const handleFilterChange = (_filters: FilterState) => {
    setResult(null);
    setError(null);
  };

  // Handle analysis request
  const handleAnalysisRequest = async (filters: FilterState, additionalInfoText: string) => {
    if (!user?.email || !user?.uid) {
      setError('Nejste přihlášeni. Prosím přihlaste se.');
      return;
    }

    // Check credits (admins have unlimited)
    if (!isAdmin && credits <= 0) {
      setError('Nemáte dostatek kreditů pro spuštění analýzy. Kontaktujte administrátora.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      // First call the backend API
      const response = await requestAnalysis({
        email: user.email,
        municipality_code: filters.municipalityCode,
        election_type: filters.electionType,
        client_party: filters.party || undefined,
        additional_info: additionalInfoText || undefined,
        user_id: user.uid,
        social_pulse: filters.socialPulse || false,
        client_party_is_new: filters.clientPartyIsNew,
      });

      // Only deduct credit AFTER successful API call (not for admins)
      if (!isAdmin) {
        console.log('[FilterPage] API call successful, now deducting credit. Current:', credits);
        const success = await deductCredit(user.uid);
        console.log('[FilterPage] Deduct result:', success);
        if (!success) {
          // Analysis started but credit deduction failed - log but don't show error
          console.warn('[FilterPage] Credit deduction failed but analysis is running');
        } else {
          // Update local credit count immediately
          const newCredits = credits - 1;
          console.log('[FilterPage] Setting new credits:', newCredits);
          setCredits(newCredits);
        }
      }

      setResult({
        success: true,
        message: `Analýza spuštěna! Výsledky budou odeslány na ${user.email}.`,
        taskId: response.task_id,
        details: response
      });
      // Show success modal and reset form
      setShowSuccessModal(true);
      setResetTrigger(prev => prev + 1);
      setAdditionalInfo('');
      try { sessionStorage.removeItem('phoenix_additional_info'); } catch {}
    } catch (err: any) {
      console.error('Analysis request error:', err);
      setError(err.message || 'Chyba při spouštění analýzy');
      // Credit was NOT deducted because we moved deduction after API success
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{
          background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                style={{
                  background: 'linear-gradient(135deg, var(--phoenix-400) 0%, var(--phoenix-500) 100%)',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                }}
              >
                <LogoIcon />
              </div>
              <div>
                <h1 className="font-display text-lg font-semibold text-white tracking-tight">
                  Phoenix Analytics
                </h1>
                <p className="text-xs text-white/60">Volební analýza v6.4</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              <Link
                to="/filter"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white"
                style={{ background: 'rgba(255, 255, 255, 0.15)' }}
              >
                <span className="flex items-center gap-2">
                  <ChartIcon />
                  Analýza
                </span>
              </Link>
              <Link
                to="/candidate"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
              >
                <span className="flex items-center gap-2">
                  <CandidateIcon />
                  Kandidát
                </span>
              </Link>
              <Link
                to="/survey"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
              >
                <span className="flex items-center gap-2">
                  <SurveyIcon />
                  Dotazník
                </span>
              </Link>
              <Link
                to="/billing"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
              >
                <span className="flex items-center gap-2">
                  <CreditCardIcon />
                  Platby
                </span>
              </Link>
              <Link
                to="/help"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
              >
                <span className="flex items-center gap-2">
                  <HelpIcon />
                  Nápověda
                </span>
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
                >
                  <span className="flex items-center gap-2">
                    <AdminIcon />
                    Admin
                  </span>
                </Link>
              )}
            </nav>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 px-3 py-2 rounded-xl transition-all hover:bg-white/10"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                  style={{ background: 'rgba(255, 255, 255, 0.2)' }}
                >
                  <UserIcon />
                </div>
                <span className="hidden sm:block text-sm text-white/90 max-w-[150px] truncate">
                  {user?.email}
                </span>
                <svg
                  className={`w-4 h-4 text-white/60 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              {/* Dropdown */}
              {showUserMenu && (
                <div
                  className="absolute right-0 mt-2 w-56 rounded-xl overflow-hidden"
                  style={{
                    background: 'var(--bg-secondary)',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-light)' }}>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      Přihlášen jako
                    </p>
                    <p className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>
                      {user?.email}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Kredity:
                      </span>
                      <span
                        className="px-2 py-0.5 rounded-full text-sm font-semibold"
                        style={{
                          background: isAdmin ? 'var(--accent-primary)' : (credits > 0 ? '#10B981' : '#EF4444'),
                          color: 'white'
                        }}
                      >
                        {isAdmin ? '∞' : credits}
                      </span>
                    </div>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-red-50"
                      style={{ color: 'var(--danger-600)' }}
                    >
                      <LogoutIcon />
                      Odhlásit se
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden border-t border-white/10">
          <div className="flex">
            <Link
              to="/filter"
              className="flex-1 py-3 text-center text-sm font-medium text-white bg-white/10"
            >
              Analýza
            </Link>
            <Link
              to="/survey"
              className="flex-1 py-3 text-center text-sm font-medium text-white/70"
            >
              Dotazník
            </Link>
            <Link
              to="/candidate"
              className="flex-1 py-3 text-center text-sm font-medium text-white/70"
            >
              Kandidát
            </Link>
            <Link
              to="/billing"
              className="flex-1 py-3 text-center text-sm font-medium text-white/70"
            >
              Platby
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                className="flex-1 py-3 text-center text-sm font-medium text-white/70"
              >
                Admin
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2
            className="font-display text-2xl lg:text-3xl font-semibold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            Volební analýza
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Vyberte parametry voleb pomocí filtrů níže. Výsledky budou odeslány na váš email.
          </p>
        </div>

        {/* Credit Info Box - show warning when low/no credits */}
        {!isAdmin && credits <= 0 && (
          <div
            className="rounded-xl p-4 mb-6 flex items-start gap-3"
            style={{
              background: 'var(--danger-100)',
              border: '1px solid var(--danger-300)'
            }}
          >
            <span style={{ color: 'var(--danger-600)' }}><AlertIcon /></span>
            <div>
              <p className="font-medium" style={{ color: 'var(--danger-700)' }}>
                Nemáte žádné kredity
              </p>
              <p className="text-sm mt-1" style={{ color: 'var(--danger-600)' }}>
                Pro spuštění analýzy potřebujete alespoň 1 kredit.{' '}
                <Link
                  to="/billing"
                  className="underline font-medium hover:no-underline"
                  style={{ color: 'var(--danger-700)' }}
                >
                  Koupit kredity →
                </Link>
              </p>
            </div>
          </div>
        )}

        {/* Credit Info - show remaining credits */}
        {!isAdmin && credits > 0 && (
          <div
            className="rounded-xl p-4 mb-6 flex items-center gap-3"
            style={{
              background: 'var(--success-50)',
              border: '1px solid var(--success-200)'
            }}
          >
            <span
              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
              style={{ background: 'var(--success-500)' }}
            >
              {credits}
            </span>
            <div>
              <p className="text-sm" style={{ color: 'var(--success-700)' }}>
                Máte <strong>{credits}</strong> {credits === 1 ? 'kredit' : credits < 5 ? 'kredity' : 'kreditů'}. Každá analýza spotřebuje 1 kredit.
              </p>
            </div>
          </div>
        )}

        {/* Filter Component */}
        <ElectionFilter
          onFilterChange={handleFilterChange}
          onAnalysisRequest={handleAnalysisRequest}
          isLoading={isLoading}
          additionalInfo={additionalInfo}
          onAdditionalInfoChange={setAdditionalInfo}
          resetTrigger={resetTrigger}
        />

        {/* Error Message */}
        {error && (
          <div
            className="rounded-xl p-4 mb-6 flex items-start gap-3"
            style={{
              background: 'var(--danger-100)',
              border: '1px solid var(--danger-500)'
            }}
          >
            <span style={{ color: 'var(--danger-600)' }}><AlertIcon /></span>
            <div>
              <p className="font-medium" style={{ color: 'var(--danger-600)' }}>
                Chyba při zpracování
              </p>
              <p className="text-sm mt-1" style={{ color: 'var(--danger-600)' }}>
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Success indicator - small banner when modal is closed */}
        {result?.success && !showSuccessModal && (
          <div
            className="rounded-xl p-4 mb-6 flex items-center justify-between"
            style={{
              background: 'var(--success-100)',
              border: '1px solid var(--success-500)'
            }}
          >
            <div className="flex items-center gap-3">
              <span style={{ color: 'var(--success-600)' }}><CheckCircleIcon /></span>
              <div>
                <p className="font-semibold" style={{ color: 'var(--success-600)' }}>
                  Analýza běží - výsledky dorazí na {user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowSuccessModal(true)}
              className="text-sm font-medium px-3 py-1.5 rounded-lg transition-all hover:bg-green-200"
              style={{ color: 'var(--success-700)' }}
            >
              Zobrazit detail
            </button>
          </div>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {/* How it works */}
          <div
            className="rounded-2xl p-6"
            style={{
              background: 'var(--bg-secondary)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          >
            <h3
              className="font-display text-lg font-semibold mb-6 flex items-center gap-2"
              style={{ color: 'var(--text-primary)' }}
            >
              <span
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: 'var(--phoenix-100)', color: 'var(--phoenix-600)' }}
              >
                ?
              </span>
              Jak to funguje?
            </h3>

            <div className="space-y-4">
              {[
                { num: 1, title: 'Vyberte parametry', desc: 'Zvolte typ voleb a obec/okres. Můžete vybrat konkrétní stranu.' },
                { num: 2, title: 'Spusťte analýzu', desc: 'Systém zpracuje data z ČSÚ a vytvoří interaktivní dashboard.' },
                { num: 3, title: 'Obdržíte výsledky', desc: 'Na email dorazí 2 zprávy: rychlá analýza (do 5 min) a hloubková analýza (do 1 hodiny).' }
              ].map((step) => (
                <div key={step.num} className="flex items-start gap-4">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm"
                    style={{
                      background: 'linear-gradient(135deg, var(--phoenix-500), var(--phoenix-600))',
                      color: 'white'
                    }}
                  >
                    {step.num}
                  </div>
                  <div>
                    <h4 className="font-medium" style={{ color: 'var(--text-primary)' }}>
                      {step.title}
                    </h4>
                    <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div
            className="rounded-2xl p-6"
            style={{
              background: 'var(--bg-secondary)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          >
            <h3
              className="font-display text-lg font-semibold mb-6 flex items-center gap-2"
              style={{ color: 'var(--text-primary)' }}
            >
              <span
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: 'var(--success-100)', color: 'var(--success-600)' }}
              >
                <CheckCircleIcon />
              </span>
              Co analýza obsahuje?
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                'Volební výsledky stran',
                'Interaktivní grafy',
                'Phoenix metriky (HHI, Gini)',
                'Koaliční potenciál',
                'Preferenční hlasy',
                'Export pro AI'
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2">
                  <span style={{ color: 'var(--success-500)' }}>
                    <CheckCircleIcon />
                  </span>
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {feature}
                  </span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer
        className="mt-12 py-8"
        style={{
          background: 'linear-gradient(135deg, var(--phoenix-800) 0%, var(--phoenix-900) 100%)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                style={{ background: 'rgba(255, 255, 255, 0.1)' }}
              >
                <LogoIcon />
              </div>
              <span className="text-white/80 text-sm">
                Phoenix Electoral Analytics v6.4
              </span>
            </div>
            <div className="text-center md:text-right">
              <p className="text-white/60 text-sm">
                Data: ČSÚ (Český statistický úřad)
              </p>
              <p className="text-white/40 text-xs mt-1">
                © 2025 Milan Pecka
              </p>
              <a
                href="mailto:pecka@dwx.cz"
                className="text-blue-400 hover:text-blue-300 text-xs mt-1 inline-block transition-colors"
              >
                📧 pecka@dwx.cz
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Click outside to close menu */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {/* Loading Overlay - Simple spinner during request */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)' }}>
          <div
            className="rounded-2xl p-8 text-center"
            style={{
              background: 'var(--bg-secondary)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
            }}
          >
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  border: '4px solid var(--phoenix-100)',
                  borderTopColor: 'var(--phoenix-500)',
                  animation: 'spin 1s linear infinite'
                }}
              />
            </div>
            <p className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
              Odesílám požadavek...
            </p>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL - Requires user to close with X button */}
      {showSuccessModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)' }}
        >
          <div
            className="relative rounded-3xl p-8 max-w-lg w-full animate-in"
            style={{
              background: 'var(--bg-secondary)',
              boxShadow: '0 25px 80px -12px rgba(0, 0, 0, 0.5)',
              animation: 'modalSlideIn 0.4s ease-out'
            }}
          >
            {/* Close button - REQUIRED to dismiss */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="absolute top-4 right-4 p-2 rounded-full transition-all hover:bg-gray-100 dark:hover:bg-gray-800"
              style={{ color: 'var(--text-secondary)' }}
              aria-label="Zavřít"
            >
              <CloseIcon />
            </button>

            {/* Success icon with animation */}
            <div
              className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, var(--success-400) 0%, var(--success-600) 100%)',
                animation: 'successPulse 2s ease-in-out infinite'
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" className="w-12 h-12">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            {/* Title */}
            <h2
              className="font-display text-2xl font-bold text-center mb-2"
              style={{ color: 'var(--text-primary)' }}
            >
              Analýza byla úspěšně spuštěna!
            </h2>

            {/* Subtitle */}
            <p className="text-center mb-6" style={{ color: 'var(--text-secondary)' }}>
              Výsledky budou odeslány na váš email
            </p>

            {/* Email highlight */}
            <div
              className="text-center py-3 px-4 rounded-xl mb-6"
              style={{ background: 'var(--phoenix-50)', border: '2px solid var(--phoenix-200)' }}
            >
              <span className="font-semibold text-lg" style={{ color: 'var(--phoenix-700)' }}>
                {user?.email}
              </span>
            </div>

            {/* Email info cards */}
            <div className="space-y-4 mb-6">
              {/* Email 1 */}
              <div
                className="flex items-start gap-4 p-4 rounded-xl"
                style={{ background: 'var(--bg-tertiary)' }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--phoenix-100)', color: 'var(--phoenix-600)' }}
                >
                  <span className="font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Rychlá analýza
                  </h4>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Do <strong>5 minut</strong> - interaktivní dashboard s grafy a volebními daty
                  </p>
                </div>
                <div
                  className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium"
                  style={{ background: 'var(--success-100)', color: 'var(--success-700)' }}
                >
                  ~5 min
                </div>
              </div>

              {/* Email 2 */}
              <div
                className="flex items-start gap-4 p-4 rounded-xl"
                style={{ background: 'var(--bg-tertiary)' }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--phoenix-100)', color: 'var(--phoenix-600)' }}
                >
                  <span className="font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Hloubková analýza s AI
                  </h4>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Do <strong>1 hodiny</strong> - návrh kampaně, SWOT analýza, strategická doporučení
                  </p>
                </div>
                <div
                  className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium"
                  style={{ background: 'var(--warning-100)', color: 'var(--warning-700)' }}
                >
                  ~1 hod
                </div>
              </div>
            </div>

            {/* Task ID */}
            {result?.taskId && (
              <p className="text-xs text-center mb-6" style={{ color: 'var(--text-tertiary)' }}>
                ID úlohy: <code
                  className="px-2 py-1 rounded font-mono"
                  style={{ background: 'var(--bg-tertiary)' }}
                >
                  {result.taskId}
                </code>
              </p>
            )}

            {/* Close button at bottom */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full py-4 rounded-xl font-semibold text-white transition-all hover:opacity-90"
              style={{
                background: 'linear-gradient(135deg, var(--phoenix-500) 0%, var(--phoenix-600) 100%)',
                boxShadow: '0 4px 14px rgba(0, 70, 140, 0.3)'
              }}
            >
              Rozumím, zavřít
            </button>
          </div>
        </div>
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes successPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 15px rgba(16, 185, 129, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default FilterPage;
